import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { ArrowLeft, Lock, Play, Clock, BookOpen, Share2, CheckCircle2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const PREVIEW_SECONDS = 30;

function fmtDur(s) {
  if (!s) return '';
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m} min`;
  return `${s} sec`;
}

const LessonSharePage = () => {
  const { courseId, lessonId } = useParams();
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [previewEnded, setPreviewEnded] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);

  useEffect(() => {
    fetchMeta();
  }, [courseId, lessonId]);

  const fetchMeta = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/courses/public/${courseId}/lesson/${lessonId}`);
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || 'Lesson not available');
      }
      const j = await res.json();
      setData(j);
    } catch (e) {
      setErr(e.message || 'Failed to load lesson');
    } finally {
      setLoading(false);
    }
  };

  // Gate preview at 30 seconds
  const handleTimeUpdate = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.currentTime >= PREVIEW_SECONDS && !previewEnded) {
      v.pause();
      v.currentTime = PREVIEW_SECONDS;
      setPreviewEnded(true);
    }
  };

  const handleSeeking = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.currentTime > PREVIEW_SECONDS) {
      v.currentTime = PREVIEW_SECONDS;
      setPreviewEnded(true);
      v.pause();
    }
  };

  const restartPreview = () => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = 0;
    setPreviewEnded(false);
    v.play().catch(() => {});
  };

  const shareLink = async () => {
    const url = window.location.href;
    const title = data?.lesson?.title || 'Free Lesson Preview';
    try {
      if (navigator.share) {
        await navigator.share({ title, url });
      } else {
        await navigator.clipboard.writeText(url);
        setShareCopied(true);
        setTimeout(() => setShareCopied(false), 2200);
      }
    } catch (_e) { /* user cancelled */ }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-golden border-t-transparent rounded-full animate-spin" data-testid="lesson-share-loading" />
      </div>
    );
  }

  if (err || !data) {
    return (
      <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center px-4">
        <BookOpen className="w-16 h-16 text-gray-600 mb-4" />
        <h2 className="text-xl mb-4 text-center" data-testid="lesson-share-error">{err || 'Lesson not found'}</h2>
        <Button onClick={() => navigate('/courses')} className="bg-golden text-black hover:bg-golden/90">
          Browse Courses
        </Button>
      </div>
    );
  }

  const { course, lesson, has_video, lessons_in_course = [] } = data;
  const idx = lessons_in_course.findIndex((l) => l.id === lessonId);
  const upNext = idx >= 0 ? lessons_in_course.slice(idx + 1, idx + 4) : [];

  const discountPct = course.original_price > course.price
    ? Math.round((1 - course.price / course.original_price) * 100)
    : 0;

  const thumbUrl = course.thumbnail_path ? `${API_URL}/api/courses/files/${course.thumbnail_path}` : null;

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <header className="fixed top-0 w-full bg-black/90 backdrop-blur-sm z-50 border-b border-golden/30">
        <div className="container mx-auto px-4 py-3 flex justify-between items-center">
          <button onClick={() => navigate(`/courses/${courseId}`)} className="flex items-center gap-2 text-golden hover:text-golden-light transition-colors" data-testid="lesson-share-back">
            <ArrowLeft className="w-5 h-5" />
            <span className="hidden sm:inline">Back to Course</span>
          </button>
          <button onClick={shareLink} className="flex items-center gap-2 text-golden hover:text-white text-sm px-3 py-1.5 rounded-lg border border-golden/30 transition-colors" data-testid="lesson-share-button">
            {shareCopied ? <><CheckCircle2 className="w-4 h-4" /> Link Copied</> : <><Share2 className="w-4 h-4" /> Share</>}
          </button>
        </div>
      </header>

      <div className="pt-20 pb-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-6">

              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm">
                  <Badge className="bg-amber-500/15 text-amber-300 border-amber-500/30 uppercase tracking-wider text-[10px]">
                    Free Preview · 30 sec
                  </Badge>
                  {course.category_name && (
                    <Badge variant="outline" className="border-golden/30 text-golden text-[10px]">
                      {course.category_name}
                    </Badge>
                  )}
                </div>
                <h1 className="text-2xl sm:text-3xl font-bold leading-tight" data-testid="lesson-share-title">
                  {lesson.title}
                </h1>
                <p className="text-gray-400 text-sm">
                  From course: <Link to={`/courses/${courseId}`} className="text-golden hover:underline" data-testid="lesson-share-course-link">{course.title}</Link>
                </p>
              </div>

              {/* Preview Video / Gate */}
              <div className="rounded-xl overflow-hidden bg-gradient-to-br from-golden/20 to-golden/5 aspect-video relative" data-testid="lesson-share-player">
                {has_video ? (
                  <>
                    <video
                      ref={videoRef}
                      src={`${API_URL}/api/courses/preview-video/${lessonId}`}
                      poster={thumbUrl || undefined}
                      controls={!previewEnded}
                      controlsList="nodownload noplaybackrate"
                      disablePictureInPicture
                      onTimeUpdate={handleTimeUpdate}
                      onSeeking={handleSeeking}
                      onContextMenu={(e) => e.preventDefault()}
                      className="w-full h-full object-contain bg-black"
                      data-testid="lesson-share-video"
                    >
                      Your browser does not support video playback.
                    </video>

                    {previewEnded && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/80 backdrop-blur-sm" data-testid="lesson-share-paywall">
                        <div className="text-center p-6 max-w-md">
                          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-golden/20 flex items-center justify-center">
                            <Lock className="w-8 h-8 text-golden" />
                          </div>
                          <h3 className="text-xl font-bold text-white mb-2">Preview ended</h3>
                          <p className="text-gray-300 text-sm mb-5">
                            Enrol in <span className="text-golden font-semibold">{course.title}</span> to watch the full lesson and the entire course.
                          </p>
                          <div className="flex flex-col sm:flex-row gap-2 justify-center">
                            <Button
                              onClick={() => navigate(`/courses/${courseId}`)}
                              className="bg-golden hover:bg-golden/90 text-black font-bold"
                              data-testid="lesson-share-enroll-btn"
                            >
                              Enrol now {discountPct > 0 ? `· ${discountPct}% OFF` : ''}
                            </Button>
                            <Button
                              onClick={restartPreview}
                              variant="outline"
                              className="border-golden/40 text-golden hover:bg-golden/10"
                              data-testid="lesson-share-restart-btn"
                            >
                              Replay preview
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <div className="text-center">
                      <Play className="w-16 h-16 text-golden/30 mx-auto mb-3" />
                      <p className="text-gray-500 text-sm">Preview not available for this lesson yet.</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Lesson meta */}
              <div className="flex flex-wrap gap-3 text-xs text-gray-400">
                {lesson.duration_seconds > 0 && (
                  <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5 text-golden" /> {fmtDur(lesson.duration_seconds)} total</span>
                )}
                <span className="flex items-center gap-1"><BookOpen className="w-3.5 h-3.5 text-golden" /> {lessons_in_course.length} lessons in course</span>
              </div>

              {/* Up next */}
              {upNext.length > 0 && (
                <Card className="bg-white/5 border-golden/20" data-testid="lesson-share-upnext">
                  <CardContent className="p-5">
                    <h3 className="text-sm font-semibold text-white mb-3 uppercase tracking-wider">Up Next in the Course</h3>
                    <div className="space-y-2">
                      {upNext.map((l, i) => (
                        <Link
                          key={l.id}
                          to={`/courses/${courseId}/lesson/${l.id}`}
                          className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/5 transition-colors"
                          data-testid={`lesson-share-upnext-${i}`}
                        >
                          <span className="text-gray-500 text-xs font-mono w-6">{String(idx + 2 + i).padStart(2, '0')}</span>
                          <Lock className="w-3.5 h-3.5 text-gray-600" />
                          <span className="text-gray-300 text-sm flex-1 line-clamp-1">{l.title}</span>
                          {l.duration_seconds > 0 && (
                            <span className="text-gray-500 text-xs">{fmtDur(l.duration_seconds)}</span>
                          )}
                        </Link>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Sidebar: Course CTA */}
            <div className="lg:col-span-1">
              <div className="sticky top-24">
                <Card className="bg-white/5 border-golden/20" data-testid="lesson-share-course-card">
                  <CardContent className="p-6 space-y-4">
                    {thumbUrl && (
                      <div className="rounded-lg overflow-hidden aspect-video bg-black/40">
                        <img src={thumbUrl} alt={course.title} className="w-full h-full object-cover" />
                      </div>
                    )}
                    <div>
                      <div className="text-xs uppercase tracking-wider text-gray-500 mb-1">Full course</div>
                      <h3 className="text-lg font-bold text-white leading-snug">{course.title}</h3>
                    </div>

                    <div className="space-y-1 text-sm text-gray-300">
                      <p>Level: <span className="text-golden">{course.level || 'All levels'}</span></p>
                      <p>Language: <span className="text-golden">{course.language || 'Hindi'}</span></p>
                    </div>

                    <div className="border-t border-golden/10 pt-4">
                      <div className="flex items-baseline gap-2 mb-1">
                        <span className="text-3xl font-bold text-golden">₹{course.price?.toLocaleString()}</span>
                        {course.original_price > course.price && (
                          <span className="text-gray-500 line-through text-sm">₹{course.original_price?.toLocaleString()}</span>
                        )}
                      </div>
                      {discountPct > 0 && (
                        <Badge className="bg-red-500/20 text-red-400 border-red-500/30 text-[10px] mb-3">
                          {discountPct}% OFF · Limited time
                        </Badge>
                      )}
                      <Button
                        onClick={() => navigate(`/courses/${courseId}`)}
                        className="w-full bg-golden hover:bg-golden/90 text-black font-bold py-5"
                        data-testid="lesson-share-sidebar-enroll"
                      >
                        Enrol & Watch Full Course
                      </Button>
                    </div>

                    <div className="text-[11px] text-gray-500 leading-relaxed">
                      You're watching a free 30-second preview. The full lesson and all course content unlock after enrolment.
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </div>

      <footer className="border-t border-golden/20 py-8 px-4 bg-black/50">
        <div className="container mx-auto text-center">
          <p className="text-gray-500 text-sm">
            &copy; {new Date().getFullYear()} Upper Circuit Stocks. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LessonSharePage;
